import { useCallback, useState } from 'react';
import { cva } from 'class-variance-authority';
import { RowComponentProps } from 'react-window';
import { Player } from './PlayerFiltersView';
import { twMerge } from 'tailwind-merge';
import { Typography } from '../Typography';
import { AddPlayer, CloseRounded, Injured, RedTicket, Unavailable } from '../svgs';
import { TeamShirt } from '../TeamShirt';
import { toCurrencyPriceMillions } from '../../utils';
import clsx from 'clsx';

const getPreferredPlayerName = (
  player: Pick<Player, 'displayName' | 'firstName' | 'lastName' | 'matchName'>
): string => {
  if (player.matchName?.trim()) {
    return player.matchName.trim();
  }

  const combinedName = `${player.firstName?.trim() ?? ''} ${player.lastName?.trim() ?? ''}`.trim();
  if (combinedName) {
    return combinedName;
  }

  return player.displayName;
};

const PlayerFiltersViewItemCVA = cva([''], {
  variants: {
    selected: {
      true: 'bg-[--playerfilters-row-bg-selected]',
      false: '',
    },
  },
});

type PlayerFiltersViewRowItemMode = 'add' | 'unavailable' | 'selected' | undefined;

export interface PlayerFiltersViewRowItemProps {
  className?: string;

  players: Player[];
  sortBy: string;
  budget: number;
  selectedPlayers?: Player['id'][];
  disabledPlayerIds?: Player['id'][];
  disabledActionFeedbackPlayerId?: Player['id'];
  disabledActionFeedbackMessage?: string;
  selectedRowVariant?: 'default' | 'twelfthMan' | 'teamSelection';
  mode: 'selection' | 'removable';
  fixturesData?: Map<string | number, string>;
  onPressPlayer: (player: Player) => void;
  onPressProfile?: (player: Player) => void;
  onPressJersey?: (player: Player) => void;
  onPressAction?: (player: Player, mode: 'add' | 'remove') => void;
  getPlayerTeamLabel?: (player: Player) => string | undefined;
}

export const PlayerFiltersViewRowItem = ({
  className,
  index,
  style,
  players,
  sortBy,
  budget: _budget,
  selectedPlayers,
  disabledPlayerIds,
  disabledActionFeedbackPlayerId,
  disabledActionFeedbackMessage,
  selectedRowVariant = 'default',
  mode,
  fixturesData,
  onPressPlayer,
  onPressProfile,
  onPressJersey,
  onPressAction,
  getPlayerTeamLabel,
}: RowComponentProps<PlayerFiltersViewRowItemProps>) => {
  const player = players[index];
  const normalizedPlayerId = String(player.id);
  const selected =
    (selectedPlayers?.findIndex(pId => String(pId) === normalizedPlayerId) ?? -1) > -1;
  const disabled =
    (disabledPlayerIds?.findIndex(pId => String(pId) === normalizedPlayerId) ?? -1) > -1;
  const [isHovered, setIsHovered] = useState(false);
  const showDisabledActionFeedback =
    disabled &&
    isHovered &&
    !!disabledActionFeedbackMessage &&
    (disabledActionFeedbackPlayerId === undefined ||
      String(disabledActionFeedbackPlayerId) === normalizedPlayerId);

  const getSortByContent = useCallback(() => {
    switch (sortBy) {
      case 'percentSelected':
        return <Typography>{player.percentSelected.toString()}% |</Typography>;
      case 'totalPoints':
        return <Typography>{player.totalPoints} |</Typography>;
      case 'price':
      default:
        return null;
    }
  }, [player, sortBy]);

  const preferredPlayerName = getPreferredPlayerName(player);
  const displayPlayerName = player.displayName || preferredPlayerName;
  const teamLabel =
    getPlayerTeamLabel?.(player) ?? player.clubName ?? player.country ?? 'Unknown Team';
  const renderModeButton = useCallback(() => {
    const unavailable = false;
    switch (mode) {
      case 'removable':
        if (selected) {
          if (selectedRowVariant === 'twelfthMan' || selectedRowVariant === 'teamSelection') {
            return (
              <button
                onClick={e => {
                  e.stopPropagation();
                  onPressAction?.(player, 'remove');
                }}
                className="inline-flex size-[20px] items-center justify-center rounded-full bg-[#101419] text-white"
                aria-label={
                  selectedRowVariant === 'twelfthMan'
                    ? 'Remove selected 12th man'
                    : 'Remove selected player'
                }
              >
                <span className="text-[12px] font-semibold leading-none">×</span>
              </button>
            );
          }
          return (
            <button
              onClick={e => {
                e.stopPropagation();
                onPressAction?.(player, 'remove');
              }}
              className="text-[--color-neutral-1000] rotate-45"
            >
              <CloseRounded />
            </button>
          );
        }
        if (unavailable || disabled) {
          return <Unavailable width={20} height={20} />;
        }
        return null;
      case 'selection':
        if (selected) {
          if (selectedRowVariant === 'twelfthMan' || selectedRowVariant === 'teamSelection') {
            return (
              <button
                onClick={e => {
                  e.stopPropagation();
                  onPressAction?.(player, 'remove');
                }}
                className="inline-flex size-[20px] items-center justify-center rounded-full bg-[#101419] text-white"
                aria-label={
                  selectedRowVariant === 'twelfthMan'
                    ? 'Remove selected 12th man'
                    : 'Remove selected player'
                }
              >
                <span className="text-[12px] font-semibold leading-none">×</span>
              </button>
            );
          }
          return (
            <button
              onClick={e => {
                e.stopPropagation();
                onPressAction?.(player, 'remove');
              }}
              className="text-[--color-neutral-1000] rotate-45"
            >
              <AddPlayer />
            </button>
          );
        }
        if (unavailable || disabled) {
          return <Unavailable width={20} height={20} />;
        }
        return (
          <button
            onClick={e => {
              e.stopPropagation();
              onPressAction?.(player, 'add');
            }}
            className={twMerge([
              'text-[--color-blue-200]',
              disabled ? 'pointer-events-none opacity-40' : '',
            ])}
          >
            <AddPlayer />
          </button>
        );
      default:
        return null;
    }
  }, [mode, onPressAction, player, selected, selectedRowVariant]);

  return (
    <div
      key={player.id}
      className={twMerge([
        'flex',
        'relative',
        'flex-1',
        'flex-row',
        'items-center',
        'px-[--padding-md] py-[--padding-sm]',
        'gap-[--pxv-12]',
        PlayerFiltersViewItemCVA({
          selected,
        }),
        selected && selectedRowVariant === 'twelfthMan' ? 'bg-[#efeee3]' : '',
        disabled
          ? 'cursor-not-allowed opacity-50'
          : 'cursor-pointer transition-colors hover:bg-[#f8f8ff]',
        className,
      ])}
      style={style}
      onClick={() => onPressPlayer?.(player)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="flex flex-1 flex-col gap-[--pxv-6]">
        <div className="flex flex-1 flex-row gap-[--padding-xs]">
          <button
            type="button"
            className={twMerge(['flex', disabled ? 'pointer-events-none' : ''])}
            onClick={e => {
              e.stopPropagation();
              if (disabled) {
                return;
              }
              onPressJersey?.(player);
            }}
            aria-label={`Open ${preferredPlayerName} profile`}
          >
            <TeamShirt
              team={player.shirtKey ?? player.country}
              position={player.position}
              assetScale="1x"
              className="h-[32px] w-[32px]"
            />
          </button>
          <div className="flex min-w-0 flex-1 flex-col">
            <div className="flex flex-row gap-[--padding-xs]">
              <Typography
                variant="subheadline020"
                className={clsx('truncate select-none', !disabled && 'cursor-pointer')}
                onClick={e => {
                  e.stopPropagation();
                  if (disabled) {
                    onPressPlayer?.(player);
                    return;
                  }
                  onPressProfile?.(player);
                }}
              >
                {displayPlayerName}
              </Typography>
              {selected && selectedRowVariant === 'twelfthMan' ? (
                <span className="inline-flex size-[20px] items-center justify-center rounded-full bg-[#eec880] text-[#222526]">
                  <span className="text-[11px] leading-none">✓</span>
                </span>
              ) : null}
              {selected && selectedRowVariant === 'teamSelection' ? (
                <span className="inline-flex size-[20px] items-center justify-center rounded-full bg-[#00a651] text-white">
                  <span className="text-[11px] leading-none">✓</span>
                </span>
              ) : null}
              {player.injuryDetails ? (
                <Injured height={16} width={16} className="text-[--color-red-200]" />
              ) : player.suspensionDetails ? (
                <RedTicket height={16} width={16} />
              ) : undefined}
            </div>
            <Typography variant="paragraph010" className="truncate">
              {player.position} | {fixturesData?.get(player.id) ?? teamLabel}
            </Typography>
          </div>
        </div>
      </div>
      <div className="self-center flex items-center gap-2">
        {getSortByContent()}{' '}
        <Typography variant="subheadline040"> {toCurrencyPriceMillions(player.price)}</Typography>
      </div>
      <div className="relative flex w-[24px] shrink-0 items-center justify-end self-center">
        {renderModeButton()}
      </div>
      {showDisabledActionFeedback ? (
        <div className="pointer-events-none absolute right-[40px] top-1/2 z-10 w-[240px] -translate-y-1/2 rounded-[14px] bg-[#efefef] px-[12px] py-[10px] shadow-[0_6px_18px_rgba(16,20,25,0.16)]">
          <Typography className="text-[12px] leading-[16px] text-[#222526]">
            {disabledActionFeedbackMessage}
          </Typography>
        </div>
      ) : null}
    </div>
  );
};
